import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Modal,
  Popconfirm,
  Row,
  Space,
  Spin,
  Statistic,
  Steps,
  Tag,
  message
} from "antd";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { errorText } from "../api";
import { formatPeriod } from "../shared/money";
import Amount from "../shared/Amount";
import { useIsMobile } from "../../../../design-system/useMediaQuery";
import { createRun, deleteRun, listRuns } from "./api";

export const STEPS = ["open", "calculated", "approved", "paid", "filed"];
export const STEP_LABELS = {
  open: "Open",
  calculated: "Calculated",
  approved: "Approved",
  paid: "Paid",
  filed: "Filed"
};
const STATUS_COLOR = {
  open: "default",
  calculated: "processing",
  approved: "warning",
  paid: "success",
  filed: "green"
};

export const RunSteps = ({ status, size = "small", compact = false }) => {
  const { t } = useTranslation("accounting");
  const isMobile = useIsMobile();
  return (
    <Steps
      size={size}
      // Inline steps stay one short row on phones instead of a tall vertical list.
      type={compact && isMobile ? "inline" : "default"}
      current={STEPS.indexOf(status)}
      items={STEPS.map(key => ({ title: t(STEP_LABELS[key]) }))}
    />
  );
};

const RunsList = () => {
  const { t } = useTranslation("accounting");
  const history = useHistory();
  const [runs, setRuns] = useState([]);
  const [nextPeriod, setNextPeriod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [period, setPeriod] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listRuns();
      setRuns(res.data.runs || []);
      setNextPeriod(res.data.nextPeriod);
    } catch (err) {
      message.error(errorText(err, t("Could not load payroll runs")));
    }
    setLoading(false);
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const start = async () => {
    try {
      const res = await createRun(period ? period.format("YYYY-MM") : nextPeriod);
      setStarting(false);
      history.push(`/user/accounting/payroll/runs/${res.data.run._id}`);
    } catch (err) {
      message.error(errorText(err, t("Could not create the payroll run")));
    }
  };

  const remove = async id => {
    try {
      await deleteRun(id);
      message.success(t("Payroll run deleted"));
      load();
    } catch (err) {
      message.error(errorText(err, t("Could not delete the payroll run")));
    }
  };

  return (
    <Spin spinning={loading}>
      <div className="acct-actions" style={{ marginBottom: 16 }}>
        <Button type="primary" onClick={() => setStarting(true)}>
          {t("Start Lohnlauf")} {nextPeriod ? formatPeriod(nextPeriod) : ""}
        </Button>
      </div>
      {!runs.length && !loading ? (
        <Card>
          <Empty
            description={t("No payroll runs yet. Start the first Lohnlauf for the current month.")}
          />
        </Card>
      ) : null}
      <Row gutter={[16, 16]}>
        {runs.map(run => (
          <Col xs={24} lg={12} xxl={8} key={run._id}>
            <Card
              style={{ height: "100%" }}
              title={
                <Space wrap size={[8, 4]} style={{ whiteSpace: "normal" }}>
                  <span className="acct-wrap">
                    {t("Abrechnungsmonat")} {formatPeriod(run.period)}
                  </span>
                  <Tag color={STATUS_COLOR[run.status]}>{t(STEP_LABELS[run.status])}</Tag>
                </Space>
              }
              actions={[
                <Button
                  key="open"
                  type="link"
                  onClick={() => history.push(`/user/accounting/payroll/runs/${run._id}`)}
                >
                  {t("Open")}
                </Button>,
                ...(["open", "calculated"].includes(run.status)
                  ? [
                      <Popconfirm
                        key="delete"
                        title={t("Delete this payroll run? Locked slips are kept.")}
                        onConfirm={() => remove(run._id)}
                      >
                        <Button type="link" danger>
                          {t("Delete")}
                        </Button>
                      </Popconfirm>
                    ]
                  : [])
              ]}
            >
              <RunSteps status={run.status} compact />
              <Row gutter={[12, 12]} style={{ marginTop: 16 }}>
                <Col xs={12} sm={8}>
                  <Statistic
                    className="acct-kpi"
                    title={t("Employees")}
                    value={run.totals?.employeeCount || 0}
                  />
                </Col>
                <Col xs={12} sm={8}>
                  <Statistic
                    className="acct-kpi"
                    title={t("Gross")}
                    valueRender={() => <Amount cents={run.totals?.gross} />}
                  />
                </Col>
                <Col xs={24} sm={8}>
                  <Statistic
                    className="acct-kpi"
                    title={t("Employer cost")}
                    valueRender={() => <Amount cents={run.totals?.employerCost} strong />}
                  />
                </Col>
              </Row>
              {run.warnings?.length ? (
                <Alert
                  style={{ marginTop: 12 }}
                  type="warning"
                  showIcon
                  message={t("{{count}} warning(s)", { count: run.warnings.length })}
                />
              ) : null}
            </Card>
          </Col>
        ))}
      </Row>
      <Modal
        rootClassName="acct-module"
        open={starting}
        title={t("Start Lohnlauf")}
        onOk={start}
        onCancel={() => setStarting(false)}
        okText={t("Start")}
        rootClassName="acct-module"
      >
        <p>{t("Choose the Abrechnungsmonat. Each month can have exactly one Lohnlauf.")}</p>
        <DatePicker
          picker="month"
          style={{ width: "100%" }}
          value={period || (nextPeriod ? dayjs(`${nextPeriod}-01`) : null)}
          onChange={setPeriod}
        />
      </Modal>
    </Spin>
  );
};

export default RunsList;
